import { ChevronLeft } from 'lucide-react';
import {
  useTranslation,
  getOrderFulfillmentMethod,
  OrderStatusBadge,
  formatPublicOrderReference,
} from '@dukanti/shared';
import type { AdminOrder, TranslationKey } from '@dukanti/shared';

export interface OrderDetailHeaderProps {
  readonly order: AdminOrder;
  readonly onBack: () => void;
}

/**
 * F-022: Header for the order detail page.
 *
 * Shows the back button (ChevronLeft flips in RTL via `rtl:rotate-180`),
 * the public order reference with the full UUID in the title attribute,
 * the formatted placed date, and the order status badge.
 */
export function OrderDetailHeader({ order, onBack }: OrderDetailHeaderProps): React.JSX.Element {
  const { t, lang } = useTranslation();

  const orderReference = formatPublicOrderReference(order.id);
  const placedDate = new Intl.DateTimeFormat(lang, { dateStyle: 'medium' }).format(
    new Date(order.created_at),
  );

  return (
    <header
      data-testid="order-detail-header"
      className="flex flex-wrap items-start justify-between gap-4"
    >
      {/* Back button + order ID */}
      <div className="flex flex-col gap-1">
        <button
          type="button"
          onClick={onBack}
          data-testid="order-detail-back"
          className="text-foreground hover:text-admin-lime-hover focus-visible:ring-admin-lime inline-flex items-center gap-1.5 rounded-sm text-sm font-medium focus-visible:outline-none focus-visible:ring-2"
          aria-label={t('admin.orders.detail.backToOrders')}
        >
          <ChevronLeft aria-hidden="true" className="size-4 rtl:rotate-180" strokeWidth={2} />
          <span>{t('admin.orders.detail.backToOrders')}</span>
        </button>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground text-sm">
            {t('admin.orders.detail.orderIdLabel')}
          </span>
          <span
            data-testid="order-detail-short-id"
            title={order.id}
            className="font-display text-foreground font-semibold tracking-wide"
          >
            {orderReference}
          </span>
        </div>

        <div className="text-muted-foreground text-sm">
          <span>{t('admin.orders.detail.placedOnLabel')} </span>
          <span data-testid="order-detail-placed-date">{placedDate}</span>
        </div>
      </div>

      {/* Status badge */}
      <div className="mt-1">
        <div className="flex flex-wrap items-center gap-2">
          <OrderStatusBadge status={order.status} method={getOrderFulfillmentMethod(order)} />
          <span className="border-border rounded-full border px-2 py-1 text-xs">
            {t(
              getOrderFulfillmentMethod(order) === 'pickup'
                ? 'fulfillment.pickup'
                : 'fulfillment.delivery',
            )}
          </span>
          {order.manual_approval_status !== 'not_required' ? (
            <span className="border-border bg-card text-foreground rounded-full border px-2 py-1 text-xs font-medium">
              {t(
                `admin.orders.manualApproval.badges.${order.manual_approval_status}` as TranslationKey,
              )}
            </span>
          ) : null}
        </div>
      </div>
    </header>
  );
}
